import React from 'react';
import { Shield, FileText, Lock, AlertCircle, CheckCircle, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const TenantProtection = () => {
  const protectionFeatures = [
    {
      title: 'Comprehensive Coverage',
      description: 'Optional tenant protection plans available to safeguard your stored belongings against unforeseen events.'
    },
    {
      title: 'Secure Facilities',
      description: '24/7 video surveillance, individual unit alarms, and secure access codes provide multiple layers of protection.'
    },
    {
      title: 'Clear Documentation',
      description: 'Transparent rental agreements with clear terms and conditions for your peace of mind.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Tenant Protection Plans | Interskate Self Storage - Lewisville, TX"
        description="Protect your stored belongings with tenant protection plans at Interskate Self Storage in Lewisville, TX. Coverage options from $2,000 to $10,000 for fire, theft, and more."
        canonical="/tenant-protection"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="tenant-protection-title">
            Tenant Protection
          </h1>
          <p className="text-xl text-green-100">
            Protect your belongings with our comprehensive tenant protection plans
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Peace of Mind for Your Stored Items
            </h2>
            <p className="text-lg text-gray-600">
              While we maintain the highest security standards, we understand that protecting your
              belongings is a top priority. Our tenant protection plans offer comprehensive coverage
              options to suit your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200" data-testid="protection-feature-0">
              <div className="mb-4"><Shield className="h-8 w-8 text-green-600" /></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{protectionFeatures[0].title}</h3>
              <p className="text-gray-600">{protectionFeatures[0].description}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200" data-testid="protection-feature-1">
              <div className="mb-4"><Lock className="h-8 w-8 text-green-600" /></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{protectionFeatures[1].title}</h3>
              <p className="text-gray-600">{protectionFeatures[1].description}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200" data-testid="protection-feature-2">
              <div className="mb-4"><FileText className="h-8 w-8 text-green-600" /></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{protectionFeatures[2].title}</h3>
              <p className="text-gray-600">{protectionFeatures[2].description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Options */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Protection Plan Options
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the level of protection that best fits your needs and budget
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Basic Protection */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-200" data-testid="coverage-option-0">
              <div className="p-6 bg-gray-50">
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Basic Protection</h3>
                <p className="text-3xl font-bold text-green-600">Up to $2,000</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Fire and smoke damage</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Water damage (non-flood)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Theft protection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Vandalism coverage</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Standard Protection */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-green-500 transform scale-105" data-testid="coverage-option-1">
              <div className="p-6 bg-green-600 text-white">
                <h3 className="text-2xl font-bold mb-2 text-white">Standard Protection</h3>
                <p className="text-3xl font-bold text-white">Up to $5,000</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">All Basic Protection benefits</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Natural disaster coverage</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Extended vandalism protection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Building collapse coverage</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Premium Protection */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-200" data-testid="coverage-option-2">
              <div className="p-6 bg-gray-50">
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Premium Protection</h3>
                <p className="text-3xl font-bold text-green-600">Up to $10,000</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">All Standard Protection benefits</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Comprehensive coverage</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Highest protection level</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Priority claims processing</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-50 rounded-xl p-8 border border-green-200">
            <div className="flex items-start mb-6">
              <AlertCircle className="h-8 w-8 text-green-600 mr-4 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Important Information
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start" data-testid="important-info-0">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Protection plans are optional but highly recommended</span>
                  </li>
                  <li className="flex items-start" data-testid="important-info-1">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Coverage begins on your move-in date</span>
                  </li>
                  <li className="flex items-start" data-testid="important-info-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Monthly protection fees are added to your rental payment</span>
                  </li>
                  <li className="flex items-start" data-testid="important-info-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Existing homeowners or renters insurance may provide coverage</span>
                  </li>
                  <li className="flex items-start" data-testid="important-info-4">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Claims must be filed within 30 days of discovery</span>
                  </li>
                  <li className="flex items-start" data-testid="important-info-5">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Detailed inventory of stored items is recommended</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Existing Insurance */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Already Have Insurance?
            </h2>
            <p className="text-gray-700 mb-4">
              If you already have homeowners or renters insurance, you may have coverage for items in storage.
              We recommend checking with your insurance provider to understand your current coverage limits
              and whether additional protection is needed.
            </p>
            <p className="text-gray-700 mb-6">
              To waive our tenant protection plan, you'll need to provide proof of existing insurance coverage
              that includes items in storage. Please bring documentation when you rent your unit.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-gray-700 font-medium">
                <strong>Note:</strong> Without tenant protection or proof of existing insurance, you'll be
                responsible for any loss or damage to your stored items.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6" data-testid="faq-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Is tenant protection required?
              </h3>
              <p className="text-gray-700">
                While not mandatory, tenant protection is highly recommended. If you decline our protection
                plan, you must provide proof of existing insurance coverage.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6" data-testid="faq-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                What if I need to file a claim?
              </h3>
              <p className="text-gray-700">
                Contact our office immediately if you discover damage or loss. We'll guide you through the
                claims process and provide all necessary documentation.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6" data-testid="faq-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Can I change my protection level?
              </h3>
              <p className="text-gray-700">
                Yes, you can upgrade or downgrade your protection plan at any time. Changes will take effect
                at the start of your next billing cycle.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6" data-testid="faq-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                What's not covered?
              </h3>
              <p className="text-gray-700">
                Standard exclusions include damage from pests, mold, inherent defects in stored items, and
                acts of war. Please review the complete policy for all exclusions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Phone className="h-12 w-12 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Questions About Tenant Protection?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Our team is here to help you understand your coverage options and choose the best protection plan.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-3 text-lg font-semibold text-blue-600 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="contact-us-cta"
            >
              Contact Us
            </a>
            <a
              href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-3 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="rent-unit-cta"
            >
              Rent a Unit
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TenantProtection;
